import { NormalizedNumber } from '@sparkdotfi/common-universal'
import { RefObject, useRef, useState } from 'react'
import { flushSync } from 'react-dom'

import { AccountConfig } from '@/config/chain/types'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
export const SAVINGS_CHART_IDS = {
  savingsRate: 'savings-rate',
  savingsBackedBy: 'savings-backed-by',
  liquidity: 'liquidity',
  collateralAtRisk: 'collateral-at-risk',
} as const

export type SavingsChartId = (typeof SAVINGS_CHART_IDS)[keyof typeof SAVINGS_CHART_IDS]

const savingsChartIdsSet = new Set<SavingsChartId>(Object.values(SAVINGS_CHART_IDS))

export function isSavingsChartId(value: string): value is SavingsChartId {
  return savingsChartIdsSet.has(value as SavingsChartId)
}

import {
  UseCollateralAtRiskDataResult,
  useCollateralAtRiskData,
} from '@/features/savings/data/savings-charts/useCollateralAtRiskData'
import {
  UseSavingsBackedByChartDataResult,
  useSavingsBackedByChartData,
} from '@/features/savings/data/savings-charts/useSavingsBackedByChartData'
import {
  UseSavingsLiquidityChartDataResult,
  useSavingsLiquidityChartData,
} from '@/features/savings/data/savings-charts/useSavingsLiquidityChartData'
import {
  UseSavingsRateInfoResult,
  useSavingsRateInfo,
} from '@/features/savings/data/savings-charts/useSavingsRateInfo/useSavingsRateInfo'
import { useTimestamp } from '@/utils/useTimestamp'

interface UseSavingsChartsParams {
  chainId: number
  selectedSavingsTokenSymbol: TokenSymbol
  savingsToken: Token
  selectedAccountConfig: AccountConfig | undefined
  liquidityChartImmediateLiquidity: NormalizedNumber | undefined
  liquidityChartSystemAvailableLiquidity: NormalizedNumber | undefined
}

interface AccountChartsProps {
  selectedChartTabId?: SavingsChartId
  onSelectedChartTabIdChange: (tabId: SavingsChartId) => void
  chartsContainerRef: RefObject<HTMLDivElement>
  highlightKey: number
  onHighlightEnd: () => void
}

interface GeneralStatsChartsProps {
  onLiquiditySparkVaultsV2MoreInfoClick: () => void
  isLiquiditySparkVaultsV2Interactive: boolean
}

export interface SavingsChartDataById {
  [SAVINGS_CHART_IDS.savingsRate]: UseSavingsRateInfoResult
  [SAVINGS_CHART_IDS.savingsBackedBy]: UseSavingsBackedByChartDataResult
  [SAVINGS_CHART_IDS.liquidity]: UseSavingsLiquidityChartDataResult
  [SAVINGS_CHART_IDS.collateralAtRisk]: UseCollateralAtRiskDataResult | undefined
}

export type SavingsChartEnabledById = Record<SavingsChartId, boolean>

export interface UseSavingsChartsResult {
  chartDataById: SavingsChartDataById
  isChartEnabledById: SavingsChartEnabledById
  accountChartsProps: AccountChartsProps
  generalStatsChartsProps: GeneralStatsChartsProps
}

const REFRESH_INTERVAL_IN_MS = 60 * 60 * 1_000 // 1 hour

export function useSavingsCharts({
  chainId,
  selectedSavingsTokenSymbol,
  savingsToken,
  selectedAccountConfig,
  liquidityChartImmediateLiquidity,
  liquidityChartSystemAvailableLiquidity,
}: UseSavingsChartsParams): UseSavingsChartsResult {
  const [selectedChartTabId, setSelectedChartTabId] = useState<SavingsChartId>()
  const [highlightKey, setHighlightKey] = useState(0)
  const chartsContainerRef = useRef<HTMLDivElement>(null)
  const { timestamp } = useTimestamp({ refreshIntervalInMs: REFRESH_INTERVAL_IN_MS })

  const savingsBackedByChartData = useSavingsBackedByChartData({ chainId, selectedAccountConfig })
  const savingsLiquidityChartData = useSavingsLiquidityChartData({ selectedAccountConfig })
  const collateralAtRiskChartData = useCollateralAtRiskData({
    enabled: selectedSavingsTokenSymbol === TokenSymbol('stUSDS'),
  })
  const savingsRateChartData = useSavingsRateInfo({
    savingsToken,
    currentTimestamp: timestamp,
    staleTime: REFRESH_INTERVAL_IN_MS,
    savingsRateQueryOptions: selectedAccountConfig?.savingsRateQueryOptions,
    historyCutoff: selectedAccountConfig?.historyCutoff,
    chainId,
  })
  const chartDataById: SavingsChartDataById = {
    [SAVINGS_CHART_IDS.savingsRate]: savingsRateChartData,
    [SAVINGS_CHART_IDS.savingsBackedBy]: savingsBackedByChartData,
    [SAVINGS_CHART_IDS.liquidity]: {
      ...savingsLiquidityChartData,
      immediateLiquidity: liquidityChartImmediateLiquidity,
      systemAvailableLiquidity: liquidityChartSystemAvailableLiquidity,
    },
    [SAVINGS_CHART_IDS.collateralAtRisk]: collateralAtRiskChartData,
  }
  const isChartEnabledById: SavingsChartEnabledById = {
    [SAVINGS_CHART_IDS.savingsRate]: !!selectedAccountConfig?.savingsRateQueryOptions,
    [SAVINGS_CHART_IDS.savingsBackedBy]: savingsBackedByChartData.shouldDisplayChart,
    [SAVINGS_CHART_IDS.liquidity]: savingsLiquidityChartData.shouldDisplayChart,
    [SAVINGS_CHART_IDS.collateralAtRisk]: collateralAtRiskChartData !== undefined,
  }
  const isLiquiditySparkVaultsV2Interactive = isChartEnabledById[SAVINGS_CHART_IDS.liquidity]

  function onLiquiditySparkVaultsV2MoreInfoClick(): void {
    if (!isLiquiditySparkVaultsV2Interactive) {
      return
    }

    flushSync(() => {
      setSelectedChartTabId(SAVINGS_CHART_IDS.liquidity)
      setHighlightKey((k) => k + 1)
    })
    chartsContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return {
    chartDataById,
    isChartEnabledById,
    accountChartsProps: {
      selectedChartTabId,
      onSelectedChartTabIdChange: setSelectedChartTabId,
      chartsContainerRef,
      highlightKey,
      onHighlightEnd: () => setHighlightKey(0),
    },
    generalStatsChartsProps: {
      onLiquiditySparkVaultsV2MoreInfoClick,
      isLiquiditySparkVaultsV2Interactive,
    },
  }
}
